import { productImageUrl } from '~Utils/images';

/**
 * The display widths ItemImage is actually asked for somewhere in EPOS -
 * menu tile, cart line, suggestion card, detail hero and variation option.
 * Kept in one place so a preload always matches what will really be
 * requested; see each component's own `*_WIDTH` constant.
 */
export const PRELOAD_IMAGE_WIDTHS = [380, 320, 200, 144, 120, 800];

/** Never throws and never blocks on a broken image - a failed preload just isn't cached. */
const preloadOne = (url: string) =>
  new Promise<void>((resolve) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = url;
  });

/**
 * Warms the browser's HTTP cache for every item/category photo, at every
 * size EPOS actually renders it at, so the first real render in any context
 * (menu tile, cart, detail pane...) is already cached instead of paying a
 * fresh CDN round trip. The kiosk stays on the menu screen all day, so
 * doing this once up front is worth it - a small batch of requests runs at
 * a time in the browser's idle time, so it never competes with a shopper's tap.
 */
export const preloadItemImages = (imageUrls: string[]): void => {
  const queue = Array.from(
    new Set(
      imageUrls
        .filter(Boolean)
        .flatMap((url) => PRELOAD_IMAGE_WIDTHS.map((width) => productImageUrl(url, width))),
    ),
  );

  const CONCURRENCY = 4;

  const runNext = () => {
    const next = queue.shift();
    if (!next) {
      return;
    }
    preloadOne(next).then(runNext);
  };

  const start = () => {
    for (let i = 0; i < CONCURRENCY; i += 1) {
      runNext();
    }
  };

  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(start, { timeout: 4000 });
  } else {
    window.setTimeout(start, 500);
  }
};
